import { ReactElement, RefObject, useRef, useState, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { PanelMenuItem } from './types';
import Tooltip from '@/components/Tooltip';

type HoverMenuButtonProps = {
    className?: string;
    title?: string;
    ariaLabel?: string;
    items?: PanelMenuItem[];
    anchorRef?: RefObject<HTMLDivElement | null>;
};

/**
 * HoverMenuButton - 共通メニューボタン
 * ２つ以上のメニュー項目が存在する場合に、「開いた冊子」アイコンを表示し、ホバーでメニューを展開。
 */
export default function HoverMenuButton({
    className,
    title,
    ariaLabel,
    items,
    anchorRef,
}: HoverMenuButtonProps): ReactElement {
    const [open, setOpen] = useState(false);
    const closeTimerRef = useRef<number | null>(null);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    const handleEnter = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setOpen(true);
    };
    const handleLeave = () => {
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 250);
    };
    const fallbackItems: PanelMenuItem[] = [
        {
            key: 'item-1',
            label: 'メニュー1',
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="6" fill="#9CA3AF" />
                </svg>
            ),
        },
        {
            key: 'item-2',
            label: 'メニュー2',
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" fill="#9CA3AF" />
                </svg>
            ),
        },
        {
            key: 'item-3',
            label: 'メニュー3',
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 6l6 12H6z" fill="#9CA3AF" />
                </svg>
            ),
        },
    ];

    const menuItems: PanelMenuItem[] = items && items.length ? items : fallbackItems;

    // メニューの位置を計算
    const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

    useEffect(() => {
        if (open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                right: window.innerWidth - rect.right,
                zIndex: 10000,
            });
        }
    }, [open]);

    return (
        <div
            ref={(el) => {
                buttonRef.current = el;
                if (anchorRef) {
                    (anchorRef as any).current = el;
                }
            }}
            className={`relative inline-block ${className ?? ''}`}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
        >
            <button
                type="button"
                className="inline-flex items-center justify-center rounded text-gray-500 hover:bg-gray-100"
                style={{ width: '32px', height: '32px' }}
                aria-label={ariaLabel ?? 'menu'}
                aria-expanded={open}
                title={title}
                onClick={() => {
                    if (closeTimerRef.current) {
                        window.clearTimeout(closeTimerRef.current);
                        closeTimerRef.current = null;
                    }
                    setOpen((v) => !v);
                }}
                >
                {/* 3点リーダー風のメニューアイコン（テキストカラーに追従） */}
                <svg
                    width="60%"
                    height="60%"
                    viewBox="4.6 5.6 16.4 12.8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <circle cx="6" cy="7" r="1.4" fill="currentColor" />
                    <rect x="9" y="6.2" width="12" height="1.6" fill="currentColor" />

                    <circle cx="6" cy="12" r="1.4" fill="currentColor" />
                    <rect x="9" y="11.2" width="12" height="1.6" fill="currentColor" />

                    <circle cx="6" cy="17" r="1.4" fill="currentColor" />
                    <rect x="9" y="16.2" width="12" height="1.6" fill="currentColor" />
                </svg>
            </button>

            {/* ドロップダウンメニュー（ホバーで表示） - ポータルで表示 */}
            {open &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="w-64 rounded-md border border-gray-200 bg-white/95 shadow"
                        role="menu"
                        aria-orientation="vertical"
                        style={menuStyle}
                        onMouseEnter={handleEnter}
                        onMouseLeave={handleLeave}
                    >
                        <ul className="py-1">
                            {menuItems.map((it) => (
                                <li key={it.key}>
                                    {it.customRender ? (
                                        <div
                                            className="px-3 py-2 text-sm flex items-center gap-2 text-gray-500 hover:bg-gray-100"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {typeof it.customRender === 'function'
                                                ? it.customRender(() => setOpen(false))
                                                : it.customRender}
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${it.disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-100'}`}
                                            role="menuitem"
                                            onClick={() => {
                                                if (it.disabled) return;
                                                if (closeTimerRef.current)
                                                    window.clearTimeout(closeTimerRef.current);
                                                setOpen(false);
                                                it.onClick?.();
                                            }}
                                            aria-disabled={it.disabled || undefined}
                                            tabIndex={it.disabled ? -1 : 0}
                                        >
                                            {it.icon && (
                                                <span className="flex-shrink-0">{it.icon}</span>
                                            )}
                                            <span className="flex-1 whitespace-normal break-words">
                                                {it.label}
                                            </span>
                                            {it.trailingIcon && (
                                                <span className="flex-shrink-0 ml-1">
                                                    {it.tooltip ? (
                                                        <Tooltip content={it.tooltip}>
                                                            <span className="inline-flex items-center">
                                                                {it.trailingIcon}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        it.trailingIcon
                                                    )}
                                                </span>
                                            )}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
